import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon, EditIcon, RepeatIcon } from "@chakra-ui/icons";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import ProductApi from "api/product";
import { convertToJalali } from "utils/helper";
import EditAccountModal from "components/Modal/EditAccountModal";
import Pagination from "components/Pagination/Pagination";

const SharedProductAccounts = () => {
  const { productId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();

  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [productName, setProductName] = useState("");

  const [currentAccount, setCurrentAccount] = useState(null);
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();

  const maxWorderId = new URLSearchParams(location.search).get("max_worder_id") || "";

  useEffect(() => {
    fetchSharedAccounts(currentPage);
  }, [productId, currentPage, location.search]);

  const fetchSharedAccounts = async (page = 1) => {
    setIsLoading(true);
    try {
      const params = { page };
      if (maxWorderId) params.max_worder_id = maxWorderId;

      const response = await ProductApi.SharedAccountsByProduct(productId, params);
      const payload = response?.data;
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setAccounts(list);
      setTotalPages(payload?.meta?.last_page || 1);
      setPerPage(payload?.meta?.per_page || list.length || 10);
      setProductName(payload?.product_name || payload?.product?.name || "");
    } catch (error) {
      toast({
        title: "خطا در دریافت اکانت های محصول",
        description: error?.response?.data?.message || "اتصال به سرور برقرار نشد.",
        status: "error",
        duration: 3500,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const regionLabel = (region) => {
    if (region === "us") return "🇺🇸";
    if (region === "tr") return "🇹🇷";
    return region ? String(region).toUpperCase() : "-";
  };

  const handleEdit = (account) => {
    setCurrentAccount(account);
    onEditModalOpen();
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }} gap={6} w="100%">
      <Card overflowX={{ sm: "scroll", xl: "hidden" }} w="100%">
        <CardHeader p="6px 0px 22px 0px" display="flex" alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap={3}>
            <Button size="sm" leftIcon={<ArrowBackIcon />} onClick={() => history.push("/admin/accounts/shared")}>
              بازگشت
            </Button>
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              اکانت های {productName || `محصول #${productId}`}
            </Text>
            <Badge colorScheme="purple">تعداد: {accounts.length}</Badge>
          </Flex>
          <Button leftIcon={<RepeatIcon />} onClick={() => fetchSharedAccounts(currentPage)} isLoading={isLoading} size="sm">
            بروزرسانی
          </Button>
        </CardHeader>
        <CardBody w="100%">
          <Box overflowX="auto" w="100%">
            <Table variant="simple" color={textColor} minWidth="860px" w="100%">
              <Thead>
                <Tr color="gray.400">
                  <Th color="gray.400">ردیف</Th>
                  <Th color="gray.400">ایمیل</Th>
                  <Th color="gray.400">تاریخ</Th>
                  <Th color="gray.400">ریجن</Th>
                  <Th color="gray.400">وضعیت</Th>
                  <Th color="gray.400"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {accounts.map((account, index) => (
                  <Tr key={account.id ?? `${account.email}-${account.created_at}`}>
                    <Td>{(currentPage - 1) * perPage + index + 1}</Td>
                    <Td>{account.email ?? "-"}</Td>
                    <Td>{account.created_at ? convertToJalali(account.created_at) : "-"}</Td>
                    <Td style={{ fontSize: "1.3em" }}>{regionLabel(account.region)}</Td>
                    <Td>
                      {account.is_shareable || account.is_shared ? (
                        <Badge colorScheme="green" variant="solid" fontSize="sm" p={2} borderRadius="md">
                          قابل اشتراک
                        </Badge>
                      ) : (
                        <Badge colorScheme="gray" variant="subtle" fontSize="sm" p={2} borderRadius="md">
                          عادی
                        </Badge>
                      )}
                    </Td>
                    <Td textAlign="center">
                      <IconButton
                        size="sm"
                        colorScheme="blue"
                        aria-label="Edit"
                        icon={<EditIcon />}
                        onClick={() => handleEdit(account)}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          {!isLoading && accounts.length === 0 && (
            <Box mt={4}>
              <Text color="gray.500">اکانت قابل اشتراکی برای این محصول یافت نشد.</Text>
            </Box>
          )}
        </CardBody>
      </Card>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {currentAccount && (
        <EditAccountModal
          isOpen={isEditModalOpen}
          onClose={onEditModalClose}
          accountId={currentAccount.id}
          productName={productName || currentAccount.product_name}
        />
      )}
    </Flex>
  );
};

export default SharedProductAccounts;
